import { useQuery } from "@tanstack/react-query";
import { ApiService } from "./api.service";
import { IResponse } from "../models/response";
import { userId } from "./index";
import { NotificationModel } from "../models/notifications/notification.model";
import { AxiosError } from "axios";

/**
 * Set the device token for Firebase notifications.
 *
 * @param {string} firebaseToken - The Firebase device token.
 * @returns {Promise<any>} Response from the server after setting the device token.
 */
export const setFirebaseDeviceToken = (firebaseToken: string) => {
  const id = userId;
  const requestBody = {
    device_token: firebaseToken,
  };
  const url = `/users/${id}/device-token`;
  try {
    const data = ApiService.patch(url, requestBody);
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Enable or disable notifications for the user.
 *
 * @param {boolean} enabled - True to enable, false to disable.
 * @returns {Promise<any>} Response from the server after updating notification settings.
 */
export const updateNotificationSettings = async (request: {
	is_notification_enabled?: boolean;
	is_email_notification_enabled?: boolean;
}) => {
	const id = userId;
	const url = `/users/${id}/notification-settings`;
	try {
		const data = await ApiService.patch(url, request);
		return data;
	} catch (error) {
		throw error;
	}
};

/**
 * Set a specific notification as read or received.
 *
 * @param {string} [notificationId] - Notification ID.
 * @param {boolean} [readFlag] - True to mark as read, false to mark as received.
 * @returns {Promise<any>} Response from the server after updating notification status.
 */
export const setNotificationAsReadOrReceived = async (
  notificationId?: string,
  readFlag?: boolean
) => {
  const id = userId;
  const flag = readFlag ? "read" : "received";
  if (notificationId) {
    const url = `/users/${id}/notifications/${notificationId}/${flag}`;
    try {
      const data = await ApiService.patch(url, {});
      return data;
    } catch (error) {
      throw error;
    }
  }
};

/**
 * Hook to fetch the count of unread notifications.
 *
 * @returns {Object} A React Query object containing unread notifications count and a `refetch` function.
 */
export const useFetchUnreadNotificationsCount = () => {
  const id = userId;
  const queryKey = ["unread-notifications-count"];
  const { refetch, ...queryResult } = useQuery<IResponse<number>, AxiosError>(
    queryKey,
    async () => {
      const url = `/users/${id}/unread-notifications-count`;
      const unreadNotificationsCount = await ApiService.get(url);
      return unreadNotificationsCount;
    }
  );
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch a list of all notifications.
 *
 * @param {string} [userId] - User ID.
 * @param {number} [page=1] - Page number.
 * @param {number} [itemsPerPage=10] - Number of items per page.
 * @param {number} [id] - Optional notification ID.
 * @param {Record<string, unknown>} [filters] - Optional filters.
 * @param {Record<string, unknown>} [sort] - Optional sorting.
 * @returns {Object} A React Query object with list of notifications and a `refetch` function.
 */
export const useFetchNotifications = (
  userId?: string,
  page = 1,
  itemsPerPage = 10,
  id?: number,
  filters?: Record<string, unknown>,
  sort?: Record<string, unknown>
) => {
  const queryKey = id
    ? ["notifications", id]
    : [
        "notifications",
        userId,
        page,
        itemsPerPage,
        JSON.stringify(filters),
        sort,
      ];

  const { refetch, ...queryResult } = useQuery<
    IResponse<NotificationModel[]>,
    AxiosError
  >(queryKey, async () => {
    const url = id
      ? `/users/${userId}/notifications${id}`
      : `/users/${userId}/notifications`;

    const params = {
      page,
      per_page: itemsPerPage,
      sortField: sort?.field,
      sortDirection: sort?.sort,
      ...filters,
    };
    const notificationsData = await ApiService.get(url, { params });
    const { data } = notificationsData;
    return data;
  });
  return { ...queryResult, refetch };
};

/**
 * Hook to fetch a list of unread notifications only.
 *
 * @param {string} [userId] - User ID.
 * @param {number} [page=1] - Page number.
 * @param {number} [itemsPerPage=10] - Number of items per page.
 * @param {number} [id] - Optional notification ID.
 * @param {Record<string, unknown>} [filters] - Optional filters.
 * @param {Record<string, unknown>} [sort] - Optional sorting.
 * @returns {Object} A React Query object with list of unread notifications and a `refetch` function.
 */
export const useFetchUnreadNotifications = (
  userId?: string,
  page = 1,
  itemsPerPage = 10,
  id?: number,
  filters?: Record<string, unknown>,
  sort?: Record<string, unknown>
) => {
  const queryKey = id
    ? ["notifications", id]
    : [
        "notifications-unread",
        userId,
        page,
        itemsPerPage,
        JSON.stringify(filters),
        sort,
      ];

  const { refetch, ...queryResult } = useQuery<
    IResponse<NotificationModel[]>,
    AxiosError
  >(queryKey, async () => {
    const url = id
      ? `/users/${userId}/notifications${id}`
      : `/users/${userId}/notifications/unread`;

    const params = {
      page,
      per_page: itemsPerPage,
      sortField: sort?.field,
      sortDirection: sort?.sort,
      ...filters,
    };
    const notificationsData = await ApiService.get(url, { params });
    const { data } = notificationsData;
    return data;
  });
  return { ...queryResult, refetch };
};

/**
 * Mark all notifications for the user as read.
 *
 * @returns {Promise<any>} Response from the server after marking all as read.
 */
export const setAllNotificationsAsRead = async () => {
  const id = userId;
  const url = `/users/${id}/notifications/mark-all-as-read`;
  try {
    const data = await ApiService.patch(url);
    return data;
  } catch (error) {
    throw error;
  }
};
